/**
 * Job order ingestion dialog: upload a customer job-order document
 * (PDF/xlsx/csv), AI-parse it, review the draft against the company's
 * work-order attribute definitions, resolve revision conflicts explicitly,
 * and create (or update) the work order with its BOM part lines.
 */
import CheckCircleIcon from "@mui/icons-material/CheckCircleOutline";
import CloudUploadIcon from "@mui/icons-material/CloudUploadOutlined";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import DescriptionOutlinedIcon from "@mui/icons-material/DescriptionOutlined";
import {
	Alert,
	Box,
	CircularProgress,
	Dialog,
	DialogActions,
	DialogContent,
	DialogTitle,
	FormControlLabel,
	IconButton,
	MenuItem,
	Radio,
	RadioGroup,
	Step,
	StepLabel,
	Stepper,
	Table,
	TableBody,
	TableCell,
	TableHead,
	TableRow,
	TextField,
	Typography,
} from "@mui/material";
import { format } from "date-fns";
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useSelector } from "react-redux";

import {
	JobOrderDraft,
	JobOrderParseResponse,
	orderIngestionApi,
	RevisionAction, // eslint-disable-line sort-imports
} from "../../../../services/order-ingestion-api";
import {
	parseEnumOptions,
	WorkOrderAttributeDefinition, // eslint-disable-line sort-imports
	workOrderAttributesApi,
} from "../../../../services/work-order-attributes-api";
import {
	GhostButton,
	MonoText,
	planner,
	PlannerCard, // eslint-disable-line sort-imports
	PrimaryButton,
	SectionLabel,
	staffingColor,
	staffingSoft,
	StaffingState,
	StatPill,
} from "../../planner-ui";

// Form controls and alerts in Wilya tokens: the app theme's primary blue must
// not leak into the planning surfaces (focus ring, labels, radios).
const fieldSx = {
	"& .MuiOutlinedInput-root": {
		...planner.type.body,
		borderRadius: `${planner.radiusSm}px`,
		"& fieldset": { borderColor: planner.hairlineStrong },
		"&:hover fieldset": { borderColor: planner.navy },
		"&.Mui-focused fieldset": { borderColor: planner.accent },
	},
	"& .MuiInputLabel-root": { ...planner.type.meta, color: planner.inkMuted },
	"& .MuiInputLabel-root.Mui-focused": { color: planner.accent },
	"& .MuiFormHelperText-root": { ...planner.type.meta, color: planner.inkMuted },
};

const alertSx = (state: StaffingState) => ({
	...planner.type.body,
	borderRadius: `${planner.radiusSm}px`,
	border: `${planner.borderWidth}px solid ${staffingColor(state)}55`,
	bgcolor: staffingSoft(state),
	color: staffingColor(state),
});

// Neutral note: alloy ground + hairline, the "draft" look.
const noteSx = {
	...planner.type.body,
	borderRadius: `${planner.radiusSm}px`,
	border: `${planner.borderWidth}px solid ${planner.hairlineStrong}`,
	bgcolor: planner.unplannedSoft,
	color: planner.inkSecondary,
};

interface JobOrderIngestDialogProps {
	open: boolean;
	onClose: () => void;
	/** Called after a work order is created or replaced (reload the plan) */
	onSuccess: () => void;
	workcenters: string[];
}

const ACCEPTED_EXTENSIONS = ".pdf,.xlsx,.xls,.csv";
const MAX_FILE_SIZE_MB = 20;

type IngestStep = "upload" | "review" | "conflict" | "done";

// Map the parsed draft onto the company's attribute keys (normalized,
// so "jobOrderNo" / "fleetUnitNo" / "revisionNo" all resolve regardless of
// the exact key spelling the company configured).
const buildAttributeValues = (
	draft: JobOrderDraft,
	definitions: WorkOrderAttributeDefinition[]
): Record<string, string> => {
	const candidates: Record<string, string | undefined> = {
		joborderno: draft.jobOrderNo,
		revisionno: draft.revision,
		revision: draft.revision,
		rev: draft.revision,
		customer: draft.customer,
		soldto: draft.customer,
		vin: draft.vehicle?.vin,
		fleetunitno: draft.vehicle?.unitNo,
		unitno: draft.vehicle?.unitNo,
		fmcunitno: draft.vehicle?.unitNo,
		speccode: draft.specCode,
	};
	const values: Record<string, string> = {};
	definitions.forEach((definition) => {
		values[definition.attrKey] = candidates[definition.attrKey.toLowerCase()] ?? "";
	});
	return values;
};

const slotTitle: Record<string, string> = {
	headline: "Identity",
	identifier: "Identifiers",
	chip: "Details",
	detail: "Other",
};

const formatDateLabel = (value?: string): string => {
	if (!value) return "—";
	try {
		return format(new Date(value), "MMM d, yyyy");
	} catch {
		return value;
	}
};

const JobOrderIngestDialog: React.FC<JobOrderIngestDialogProps> = ({
	open,
	onClose,
	onSuccess,
	workcenters,
}) => {
	const companyId = useSelector((state: any) => state?.user?.userData?.companyId);

	const [step, setStep] = useState<IngestStep>("upload");
	const [file, setFile] = useState<File | null>(null);
	const [dragActive, setDragActive] = useState(false);
	const [parsing, setParsing] = useState(false);
	const [confirming, setConfirming] = useState(false);
	const [error, setError] = useState<string | null>(null);

	const [parseResponse, setParseResponse] = useState<JobOrderParseResponse | null>(null);
	const [attributeDefinitions, setAttributeDefinitions] = useState<WorkOrderAttributeDefinition[]>([]);
	const [attributeValues, setAttributeValues] = useState<Record<string, string>>({});
	const [startDate, setStartDate] = useState("");
	const [workstation, setWorkstation] = useState("");
	const [laborStandardId, setLaborStandardId] = useState<number | "">("");
	const [conflictChoice, setConflictChoice] = useState<RevisionAction>("replace");
	const [resultMessage, setResultMessage] = useState("");
	const [resultWarnings, setResultWarnings] = useState<string[]>([]);

	// Company attribute definitions drive the review form fields
	useEffect(() => {
		if (!open || !companyId) return;
		workOrderAttributesApi.getByCompany(companyId)
			.then((definitions) => setAttributeDefinitions(definitions.filter((d) => d.isActive)))
			.catch((err) => {
				console.error("Failed to fetch work order attribute definitions:", err);
				setAttributeDefinitions([]);
			});
	}, [open, companyId]);

	const draft = parseResponse?.draft;
	const existingOrder = parseResponse?.existingOrder;

	const sortedDefinitions = useMemo(
		() => [...attributeDefinitions].sort((a, b) => a.sortOrder - b.sortOrder),
		[attributeDefinitions]
	);
	const definitionGroups = useMemo(() => {
		const groups: { slot: string; definitions: WorkOrderAttributeDefinition[] }[] = [];
		["headline", "identifier", "chip", "detail"].forEach((slot) => {
			const inSlot = sortedDefinitions.filter((d) => d.displaySlot === slot);
			if (inSlot.length > 0) groups.push({ slot, definitions: inSlot });
		});
		return groups;
	}, [sortedDefinitions]);

	const totalEstHours = useMemo(() => {
		if (!draft) return 0;
		return draft.laborPhases.reduce((sum, phase) => sum + (phase.estHours || 0), 0);
	}, [draft]);

	const steps = useMemo(
		() => (existingOrder
			? ["Upload", "Review", "Resolve conflict", "Done"]
			: ["Upload", "Review", "Done"]),
		[existingOrder]
	);
	const activeStepIndex = useMemo(() => {
		if (step === "upload") return 0;
		if (step === "review") return 1;
		if (step === "conflict") return 2;
		return steps.length - 1;
	}, [step, steps]);

	const resetState = useCallback(() => {
		setStep("upload");
		setFile(null);
		setDragActive(false);
		setParsing(false);
		setConfirming(false);
		setError(null);
		setParseResponse(null);
		setAttributeValues({});
		setStartDate("");
		setWorkstation("");
		setLaborStandardId("");
		setConflictChoice("replace");
		setResultMessage("");
		setResultWarnings([]);
	}, []);

	const handleClose = useCallback(() => {
		if (parsing || confirming) return;
		resetState();
		onClose();
	}, [parsing, confirming, resetState, onClose]);

	const validateFile = (candidate: File): string | null => {
		if (candidate.size > MAX_FILE_SIZE_MB * 1024 * 1024) {
			return `File size exceeds ${MAX_FILE_SIZE_MB}MB limit`;
		}
		const extension = `.${candidate.name.split(".").pop()?.toLowerCase()}`;
		if (!ACCEPTED_EXTENSIONS.split(",").includes(extension)) {
			return "Unsupported file type. Please upload a PDF, Excel or CSV job order.";
		}
		return null;
	};

	const handleFileSelected = useCallback((candidate: File) => {
		const validationError = validateFile(candidate);
		if (validationError) {
			setError(validationError);
			return;
		}
		setError(null);
		setFile(candidate);
	}, []);

	const handleDrag = useCallback((event: React.DragEvent) => {
		event.preventDefault();
		event.stopPropagation();
		if (event.type === "dragenter" || event.type === "dragover") {
			setDragActive(true);
		} else if (event.type === "dragleave") {
			setDragActive(false);
		}
	}, []);

	const handleDrop = useCallback((event: React.DragEvent) => {
		event.preventDefault();
		event.stopPropagation();
		setDragActive(false);
		if (event.dataTransfer.files?.[0]) {
			handleFileSelected(event.dataTransfer.files[0]);
		}
	}, [handleFileSelected]);

	const handleInputChange = useCallback((event: React.ChangeEvent<HTMLInputElement>) => {
		if (event.target.files?.[0]) {
			handleFileSelected(event.target.files[0]);
		}
	}, [handleFileSelected]);

	const handleParse = useCallback(async () => {
		if (!file || !companyId) return;
		setParsing(true);
		setError(null);
		try {
			const response = await orderIngestionApi.parse(file, companyId);
			if (!response.success || !response.draft) {
				setError(response.error || "Failed to parse the document. Please try again.");
				return;
			}
			setParseResponse(response);
			setAttributeValues(buildAttributeValues(response.draft, attributeDefinitions));
			setStartDate(response.draft.startDate || "");
			setWorkstation(response.existingOrder?.workstation || "");
			setLaborStandardId(response.laborStandardCandidates[0]?.id ?? "");
			setStep("review");
		} catch (err: any) {
			console.error("Job order parse error:", err);
			setError(
				err?.response?.data?.error ||
				err?.response?.data?.message ||
				err?.message ||
				"Failed to parse the document. Please try again."
			);
		} finally {
			setParsing(false);
		}
	}, [file, companyId, attributeDefinitions]);

	const handleConfirm = useCallback(async (action: RevisionAction) => {
		if (!draft) return;
		if (action === "skip") {
			handleClose();
			return;
		}
		setConfirming(true);
		setError(null);
		try {
			const response = await orderIngestionApi.confirm({
				companyId,
				draft: { ...draft, startDate },
				workstation,
				laborStandardId: laborStandardId === "" ? undefined : laborStandardId,
				productId: parseResponse?.productMatch?.productId,
				revisionAction: action,
				existingWorkOrderId: action === "replace" ? existingOrder?.id : undefined,
				attributeValues,
			});
			setResultMessage(response.message || "Work order saved.");
			setResultWarnings(response.warnings || []);
			setStep("done");
			onSuccess();
		} catch (err: any) {
			console.error("Job order confirm error:", err);
			setError(
				err?.response?.data?.message ||
				err?.message ||
				"Failed to create the work order. Please try again."
			);
		} finally {
			setConfirming(false);
		}
	}, [
		draft,
		companyId,
		startDate,
		workstation,
		laborStandardId,
		parseResponse,
		existingOrder,
		attributeValues,
		onSuccess,
		handleClose,
	]);

	const handleReviewNext = useCallback(() => {
		if (existingOrder) {
			setStep("conflict");
			return;
		}
		handleConfirm("create");
	}, [existingOrder, handleConfirm]);

	const reviewValid = Boolean(startDate) && Boolean(workstation);
	const incomingRevision = attributeValues.revisionNo || draft?.revision || "?";
	const existingRevision = existingOrder?.revision || "?";

	const renderAttributeField = (definition: WorkOrderAttributeDefinition) => {
		const value = attributeValues[definition.attrKey] ?? "";
		const isEnum = definition.dataType === "enum";
		return (
			<TextField
				key={definition.attrKey}
				select={isEnum}
				size="small"
				fullWidth
				type={definition.dataType === "number" ? "number" : definition.dataType === "date" ? "date" : "text"}
				label={definition.label}
				required={definition.isRequired}
				value={value}
				onChange={(event) =>
					setAttributeValues((prev) => ({ ...prev, [definition.attrKey]: event.target.value }))
				}
				InputLabelProps={definition.dataType === "date" ? { shrink: true } : undefined}
				sx={fieldSx}
			>
				{isEnum
					? parseEnumOptions(definition.enumOptions).map((option) => (
						<MenuItem key={option} value={option}>
							{option}
						</MenuItem>
					))
					: null}
			</TextField>
		);
	};

	return (
		<Dialog
			open={open}
			onClose={parsing || confirming ? undefined : handleClose}
			maxWidth="md"
			fullWidth
			PaperProps={{
				sx: {
					minHeight: 520,
					borderRadius: `${planner.radius}px`,
					boxShadow: planner.shadowDialog,
				},
			}}
		>
			<DialogTitle>
				<Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
					<DescriptionOutlinedIcon sx={{ color: planner.accent }} />
					<Typography sx={{ ...planner.type.title, color: planner.ink }}>
						Import job order
					</Typography>
				</Box>
			</DialogTitle>

			<DialogContent>
				<Stepper activeStep={activeStepIndex} sx={{ mb: 3 }}>
					{steps.map((label) => (
						<Step key={label}>
							<StepLabel>{label}</StepLabel>
						</Step>
					))}
				</Stepper>

				{error ? (
					<Alert
					severity="error"
					icon={false}
					sx={{ ...alertSx("unstaffed"), mb: 2, whiteSpace: "pre-wrap" }}
					onClose={() => setError(null)}
				>
						{error}
					</Alert>
				) : null}

				{step === "upload" ? (
					<Box>
						{!file ? (
							<Box
								component="label"
								onDragEnter={handleDrag}
								onDragLeave={handleDrag}
								onDragOver={handleDrag}
								onDrop={handleDrop}
								sx={{
									display: "flex",
									flexDirection: "column",
									alignItems: "center",
									p: 5,
									border: `2px dashed ${dragActive ? planner.accent : planner.hairlineStrong}`,
									borderRadius: `${planner.radius}px`,
									bgcolor: dragActive ? planner.accentSoft : planner.surfaceSubtle,
									cursor: "pointer",
									transition: "all 0.2s ease",
									"&:hover": { borderColor: planner.accent, bgcolor: planner.accentSoft },
								}}
							>
								<input
									type="file"
									accept={ACCEPTED_EXTENSIONS}
									onChange={handleInputChange}
									style={{ display: "none" }}
								/>
								<CloudUploadIcon sx={{ fontSize: 52, color: planner.accent, mb: 1.5 }} />
								<Typography sx={{ ...planner.type.title, color: planner.ink }}>
									Drop the job order document here
								</Typography>
								<Typography sx={{ ...planner.type.body, color: planner.inkSecondary, mt: 0.5 }}>
									or click to browse — PDF, Excel or CSV, up to {MAX_FILE_SIZE_MB}MB
								</Typography>
							</Box>
						) : (
							<PlannerCard sx={{ p: 2, display: "flex", alignItems: "center", gap: 1.5 }}>
								<DescriptionOutlinedIcon sx={{ fontSize: 36, color: planner.accent }} />
								<Box sx={{ flex: 1, minWidth: 0 }}>
									<Typography sx={{ ...planner.type.bodyStrong, color: planner.ink }} noWrap>
										{file.name}
									</Typography>
									<Typography sx={{ ...planner.type.meta, color: planner.inkSecondary }}>
										{(file.size / (1024 * 1024)).toFixed(1)} MB
									</Typography>
								</Box>
								{parsing ? (
									<Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
										<CircularProgress size={18} sx={{ color: planner.accent }} />
										<Typography sx={{ ...planner.type.meta, color: planner.inkSecondary }}>
											Reading the document…
										</Typography>
									</Box>
								) : (
									<IconButton
									size="small"
									onClick={() => setFile(null)}
									sx={{ color: planner.inkMuted, "&:hover": { color: planner.unstaffed } }}
								>
										<DeleteOutlineIcon fontSize="small" />
									</IconButton>
								)}
							</PlannerCard>
						)}
						<Alert severity="info" icon={false} sx={{ ...noteSx, mt: 2.5 }}>
							Upload one job order document per vehicle (e.g. Holman&apos;s per-unit PDF).
							The header, labor phases and every parts line are extracted for review —
							nothing is saved until you confirm.
						</Alert>
					</Box>
				) : null}

				{step === "review" && draft ? (
					<Box sx={{ display: "flex", flexDirection: "column", gap: 2.5 }}>
						{parseResponse?.warnings?.length ? (
							<Alert severity="warning" icon={false} sx={alertSx("partial")}>
								{parseResponse.warnings.map((warning) => (
									<Typography key={warning} sx={{ ...planner.type.meta }}>
										{warning}
									</Typography>
								))}
							</Alert>
						) : null}

						<Box sx={{ display: "flex", gap: 1.5, flexWrap: "wrap" }}>
							<StatPill label="Job order" value={draft.jobOrderNo || "—"} />
							<StatPill label="Rev" value={incomingRevision} />
							<StatPill label="Part lines" value={draft.partLines.length} />
							<StatPill
								label="Est. hours"
								value={totalEstHours > 0 ? totalEstHours.toFixed(1) : "—"}
								hint="Total estimated labor hours across phases"
							/>
						</Box>

						{draft.description ? (
							<Box>
								<SectionLabel>Job description</SectionLabel>
								<Typography sx={{ ...planner.type.body, color: planner.inkSecondary, mt: 0.25 }}>
									{draft.description}
								</Typography>
								{draft.vehicle ? (
									<MonoText sx={{ display: "block", mt: 0.5 }}>
										{[
											draft.vehicle.year,
											draft.vehicle.make,
											draft.vehicle.model,
											draft.vehicle.vin ? `VIN ${draft.vehicle.vin}` : null,
										].filter(Boolean).join(" · ") || null}
									</MonoText>
								) : null}
							</Box>
						) : null}

						{definitionGroups.map((group) => (
							<Box key={group.slot}>
								<SectionLabel sx={{ mb: 1 }}>{slotTitle[group.slot] ?? group.slot}</SectionLabel>
								<Box
									sx={{
										display: "grid",
										gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr" },
										gap: 1.5,
									}}
								>
									{group.definitions.map(renderAttributeField)}
								</Box>
							</Box>
						))}

						<Box>
							<SectionLabel sx={{ mb: 1 }}>Schedule</SectionLabel>
							<Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr" }, gap: 1.5 }}>
								<TextField
									size="small"
									fullWidth
									type="date"
									label="Start date"
									required
									value={startDate}
									onChange={(event) => setStartDate(event.target.value)}
									InputLabelProps={{ shrink: true }}
									helperText="The order starts at 07:00 on this date"
									sx={fieldSx}
								/>
								<TextField
									select
									size="small"
									fullWidth
									label="Work center"
									required
									value={workstation}
									onChange={(event) => setWorkstation(event.target.value)}
									sx={fieldSx}
								>
									{workcenters.map((workcenter) => (
										<MenuItem key={workcenter} value={workcenter}>
											{workcenter}
										</MenuItem>
									))}
								</TextField>
							</Box>
						</Box>

						<Box>
							<SectionLabel sx={{ mb: 1 }}>Labor standard</SectionLabel>
							{parseResponse && parseResponse.laborStandardCandidates.length > 0 ? (
								<TextField
									select
									size="small"
									fullWidth
									label={`Matched by spec ${draft.specCode || ""}`.trim()}
									value={laborStandardId}
									onChange={(event) =>
										setLaborStandardId(event.target.value === "" ? "" : Number(event.target.value))
									}
									sx={fieldSx}
								>
									<MenuItem value="">No labor standard</MenuItem>
									{parseResponse.laborStandardCandidates.map((candidate) => (
										<MenuItem key={candidate.id} value={candidate.id}>
											{candidate.displayName}
										</MenuItem>
									))}
								</TextField>
							) : (
								<Alert severity="warning" icon={false} sx={alertSx("partial")}>
									No match — this order will need a labor standard before allocation.
								</Alert>
							)}
						</Box>

						<Box>
							<SectionLabel sx={{ mb: 1 }}>
								Part lines ({draft.partLines.length})
							</SectionLabel>
							{draft.partLines.length > 0 ? (
								<Box
									sx={{
										border: `${planner.borderWidth}px solid ${planner.hairlineStrong}`,
										borderRadius: `${planner.radiusSm}px`,
										maxHeight: 240,
										overflow: "auto",
									}}
								>
									<Table size="small" stickyHeader>
										<TableHead>
											<TableRow>
												<TableCell sx={{ ...planner.labelCaps, color: planner.inkMuted }}>Section</TableCell>
												<TableCell sx={{ ...planner.labelCaps, color: planner.inkMuted }}>Phase</TableCell>
												<TableCell sx={{ ...planner.labelCaps, color: planner.inkMuted }}>Part #</TableCell>
												<TableCell sx={{ ...planner.labelCaps, color: planner.inkMuted }}>Description</TableCell>
												<TableCell sx={{ ...planner.labelCaps, color: planner.inkMuted }}>Supplier</TableCell>
												<TableCell align="right" sx={{ ...planner.labelCaps, color: planner.inkMuted }}>Qty</TableCell>
											</TableRow>
										</TableHead>
										<TableBody>
											{draft.partLines.map((line, index) => (
												<TableRow key={`${line.partNumber}-${line.operationLabel}-${line.phaseCode}-${index}`}>
													<TableCell sx={{ ...planner.type.meta, color: planner.inkSecondary }}>
														{line.operationLabel || "—"}
													</TableCell>
													<TableCell sx={{ ...planner.type.meta, color: planner.inkSecondary }}>
														{line.phaseCode || "—"}
													</TableCell>
													<TableCell><MonoText>{line.partNumber}</MonoText></TableCell>
													<TableCell sx={{ ...planner.type.meta, color: planner.ink }}>
														{line.description || "—"}
													</TableCell>
													<TableCell sx={{ ...planner.type.meta, color: planner.inkSecondary }}>
														{line.supplierName || "—"}
													</TableCell>
													<TableCell align="right" sx={{ ...planner.type.metaStrong, color: planner.ink }}>
														{line.qty ?? "—"}
													</TableCell>
												</TableRow>
											))}
										</TableBody>
									</Table>
								</Box>
							) : (
								<Alert severity="info" icon={false} sx={noteSx}>
								No part lines were found in the document.
							</Alert>
							)}
						</Box>
					</Box>
				) : null}

				{step === "conflict" && draft && existingOrder ? (
					<Box sx={{ display: "flex", flexDirection: "column", gap: 2.5 }}>
						<Alert severity="warning" icon={false} sx={alertSx("partial")}>
							Job order {existingOrder.jobOrderNo || draft.jobOrderNo} is already on the plan
							as work order {existingOrder.id}. Choose how to proceed — nothing is overwritten
							without your say-so.
						</Alert>

						<Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr" }, gap: 1.5 }}>
							<PlannerCard sx={{ p: 2 }}>
								<SectionLabel>Existing order</SectionLabel>
								<Typography sx={{ ...planner.type.title, color: planner.ink, mt: 0.5 }}>
									Rev {existingRevision}
								</Typography>
								<Typography sx={{ ...planner.type.body, color: planner.inkSecondary, mt: 0.75 }}>
									Start {formatDateLabel(existingOrder.startTime)}
								</Typography>
								<Typography sx={{ ...planner.type.body, color: planner.inkSecondary }}>
									Work center {existingOrder.workstation || "—"}
								</Typography>
							</PlannerCard>
							<PlannerCard sx={{ p: 2 }} accent="partial">
								<SectionLabel>Incoming document</SectionLabel>
								<Typography sx={{ ...planner.type.title, color: planner.ink, mt: 0.5 }}>
									Rev {incomingRevision}
								</Typography>
								<Typography sx={{ ...planner.type.body, color: planner.inkSecondary, mt: 0.75 }}>
									Start {formatDateLabel(startDate)}
								</Typography>
								<Typography sx={{ ...planner.type.body, color: planner.inkSecondary }}>
									Work center {workstation || "—"}
								</Typography>
							</PlannerCard>
						</Box>

						<RadioGroup
							value={conflictChoice}
							onChange={(event) => setConflictChoice(event.target.value as RevisionAction)}
							sx={{
								"& .MuiRadio-root": { color: planner.inkMuted },
								"& .MuiRadio-root.Mui-checked": { color: planner.accent },
								"& .MuiFormControlLabel-label": { ...planner.type.body, color: planner.ink },
							}}
						>
							<FormControlLabel
								value="replace"
								control={<Radio size="small" />}
								label={`Update existing order (Rev ${existingRevision} → ${incomingRevision})`}
							/>
							<FormControlLabel
								value="create"
								control={<Radio size="small" />}
								label="Create duplicate anyway"
							/>
							<FormControlLabel
								value="skip"
								control={<Radio size="small" />}
								label="Cancel — keep the existing order untouched"
							/>
						</RadioGroup>
					</Box>
				) : null}

				{step === "done" ? (
					<Box
						sx={{
							display: "flex",
							flexDirection: "column",
							alignItems: "center",
							textAlign: "center",
							py: 5,
						}}
					>
						<CheckCircleIcon sx={{ fontSize: 48, color: planner.staffed, mb: 1.5 }} />
						<Typography sx={{ ...planner.type.title, color: planner.ink }}>
							{resultMessage}
						</Typography>
						{resultWarnings.length > 0 ? (
							<Alert
								severity="warning"
								icon={false}
								sx={{ ...alertSx("partial"), mt: 2, textAlign: "left" }}
							>
								{resultWarnings.map((warning) => (
									<Typography key={warning} sx={{ ...planner.type.meta }}>
										{warning}
									</Typography>
								))}
							</Alert>
						) : null}
						<GhostButton size="small" onClick={handleClose} sx={{ mt: 2.5 }}>
							View on work plan
						</GhostButton>
					</Box>
				) : null}
			</DialogContent>

			<DialogActions sx={{ px: 3, pb: 2 }}>
				{step === "upload" ? (
					<>
						<GhostButton size="small" onClick={handleClose} disabled={parsing}>
							Cancel
						</GhostButton>
						<PrimaryButton
							size="small"
							onClick={handleParse}
							disabled={!file || parsing || !companyId}
							startIcon={parsing ? <CircularProgress size={14} color="inherit" /> : <CloudUploadIcon sx={{ fontSize: 16 }} />}
						>
							{parsing ? "Parsing…" : "Parse document"}
						</PrimaryButton>
					</>
				) : null}

				{step === "review" ? (
					<>
						<GhostButton size="small" onClick={() => setStep("upload")} disabled={confirming}>
							Back
						</GhostButton>
						<GhostButton size="small" onClick={handleClose} disabled={confirming}>
							Cancel
						</GhostButton>
						<PrimaryButton
							size="small"
							onClick={handleReviewNext}
							disabled={!reviewValid || confirming}
							startIcon={confirming ? <CircularProgress size={14} color="inherit" /> : undefined}
						>
							{existingOrder
								? "Next: resolve conflict"
								: confirming ? "Creating…" : "Create work order"}
						</PrimaryButton>
					</>
				) : null}

				{step === "conflict" ? (
					<>
						<GhostButton size="small" onClick={() => setStep("review")} disabled={confirming}>
							Back
						</GhostButton>
						<PrimaryButton
							size="small"
							onClick={() => handleConfirm(conflictChoice)}
							disabled={confirming}
							startIcon={confirming ? <CircularProgress size={14} color="inherit" /> : undefined}
						>
							{confirming
								? "Saving…"
								: conflictChoice === "replace"
									? "Update existing order"
									: conflictChoice === "create"
										? "Create duplicate"
										: "Close without importing"}
						</PrimaryButton>
					</>
				) : null}

				{step === "done" ? (
					<PrimaryButton size="small" onClick={handleClose}>
						Done
					</PrimaryButton>
				) : null}
			</DialogActions>
		</Dialog>
	);
};

export default JobOrderIngestDialog;
